<template>
  <!-- Overview tab of a system: where it runs, its data, security and where it's used (US 9370) -->
  <div v-if="summary" class="sp-overview">
    <div class="sp-main">
      <!-- Where it runs -->
      <section class="v2-card sp-sec" qid="system-where-it-runs">
        <div class="sp-sec__head">
          <font-awesome-icon icon="globe"/>
          <h3>{{ $t('system_page.where.title') }}</h3>
          <span class="sp-pips">
            <i v-for="(done, index) in pips(['runs_as', 'location', 'contract'])" :key="index" :class="{ 'is-no': !done }"></i>
          </span>
          <span class="sp-sec__state">{{ sectionState(['runs_as', 'location', 'contract']) }}</span>
        </div>

        <div class="sp-kv">
          <div class="sp-kv__k">{{ $t('systems_overview.fields.runs_as') }}<span class="sp-req"></span></div>
          <div class="sp-kv__v">
            <div class="sp-runs" role="radiogroup">
              <button v-for="value in runsAsValues"
                  :key="value"
                  type="button"
                  role="radio"
                  :aria-checked="summary.runs_as === value ? 'true' : 'false'"
                  :class="{ on: summary.runs_as === value }"
                  :disabled="!canEdit"
                  :qid="'system-runs-as-' + value"
                  @click="setRunsAs(value)">{{ $t('systems_overview.runs_as.' + value) }}</button>
            </div>
            <small class="sp-sub">{{ runsAsHelp }}</small>
          </div>
        </div>

        <div v-if="needsLocation" class="sp-kv">
          <div class="sp-kv__k">{{ $t('systems_overview.fields.location') }}<span class="sp-req"></span></div>
          <div class="sp-kv__v">
            <b-form-input v-model="location"
                :disabled="!canEdit"
                :placeholder="$t('system_page.where.location_placeholder')"
                qid="system-location"
                @blur="saveLocation"
                @keyup.enter="saveLocation"></b-form-input>
          </div>
        </div>

        <template v-if="modules.contracts">
          <template v-for="group in contractGroups">
            <div v-if="group.links.length || group.key === 'runs_on'" :key="group.key" class="sp-group" :class="'is-' + group.key">
              {{ $t('system_page.where.groups.' + group.key) }}
            </div>
            <div v-for="link in group.links" :key="group.key + link.link_id" class="sp-kv" :class="{ 'is-unsorted': group.key === 'unsorted' }">
              <div class="sp-kv__k">{{ link.covers ? $t('systems_overview.covers.' + link.covers) : $t('systems_overview.covers.none') }}</div>
              <div class="sp-kv__v">
                <div class="sp-contract" :class="{ 'has-menu': canEditContracts }">
                  <div class="sp-contract__top">
                    <b-dropdown v-if="canEditContracts"
                        variant="link"
                        toggle-class="sp-covers"
                        :class="'is-' + group.key"
                        no-caret
                        :qid="'system-covers-' + link.link_id">
                      <template #button-content>
                        {{ link.covers ? $t('systems_overview.covers.' + link.covers) : $t('systems_overview.covers.none') }}
                        <font-awesome-icon icon="angle-down"/>
                      </template>
                      <b-dropdown-header>{{ $t('system_page.where.groups.runs_on') }}</b-dropdown-header>
                      <b-dropdown-item v-for="value in coversRunsOn" :key="value" :active="link.covers === value" @click="setCovers(link, value)">{{ $t('systems_overview.covers.' + value) }}</b-dropdown-item>
                      <b-dropdown-header>{{ $t('system_page.where.groups.access') }}</b-dropdown-header>
                      <b-dropdown-item v-for="value in coversAccess" :key="value" :active="link.covers === value" @click="setCovers(link, value)">{{ $t('systems_overview.covers.' + value) }}</b-dropdown-item>
                      <b-dropdown-divider></b-dropdown-divider>
                      <b-dropdown-item :active="!link.covers" @click="setCovers(link, null)">{{ $t('systems_overview.covers.none') }}</b-dropdown-item>
                    </b-dropdown>
                    <span v-else class="sp-covers" :class="'is-' + group.key">{{ link.covers ? $t('systems_overview.covers.' + link.covers) : $t('systems_overview.covers.none') }}</span>
                    <router-link :to="{ name: 'contracts.view', params: { slug: link.slug } }" class="sp-contract__name">{{ link.name }}</router-link>
                    <span class="sp-grow"></span>
                    <span v-if="link.processing_role" class="sp-muted">{{ $t('maintenance.third_parties.processing_role.' + $options.filters.slugify(link.processing_role)) }}</span>
                    <b-dropdown v-if="canEditContracts"
                        right
                        no-caret
                        variant="link"
                        toggle-class="sp-contract__more"
                        menu-class="sx-menu"
                        :qid="'system-contract-more-' + link.link_id">
                      <template #button-content><font-awesome-icon icon="ellipsis-h"/><span class="sr-only">{{ $t('system_hero.more') }}</span></template>
                      <b-dropdown-item @click="$emit('link-contract', link)"><font-awesome-icon icon="pen"/>{{ $t('system_link.edit_title') }}</b-dropdown-item>
                      <b-dropdown-item link-class="is-danger" @click="unlink(link)"><font-awesome-icon icon="unlink"/>{{ $t('system_link.unlink') }}</b-dropdown-item>
                    </b-dropdown>
                  </div>
                  <div class="sp-contract__facts">
                    <span v-if="link.dpa === 'Yes'" class="sp-badge is-ok"><font-awesome-icon icon="check"/>{{ $t('system_page.where.dpa') }}</span>
                    <span v-else-if="link.processing_role === 'Data processor'" class="sp-badge is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('system_page.where.no_dpa') }}</span>
                    <span v-if="link.countries.length" class="sp-chip">{{ link.countries.join(', ') }}</span>
                    <span v-if="link.sub_processors" class="sp-chip">{{ $tc('system_page.where.sub_processors', link.sub_processors, { count: link.sub_processors }) }}</span>
                    <span v-if="link.transfers === 'Yes'" class="sp-chip">{{ link.transfer_basis ? $t('system_page.where.transfers_basis', { basis: link.transfer_basis }) : $t('system_page.where.transfers') }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="group.key === 'runs_on' && !group.links.length" :key="group.key + '-empty'" class="sp-kv" :class="{ 'is-missing': missing('contract') }">
              <div class="sp-kv__k"></div>
              <div class="sp-kv__v"><span class="sp-muted">{{ needsContract ? $t('system_page.where.no_runs_on') : $t('system_page.where.no_runs_on_needed') }}</span></div>
            </div>
          </template>
          <div v-if="unsortedCount" class="sp-note">
            {{ $t('system_page.where.unsorted_note') }}
            <router-link :to="{ name: 'systems.contract_links' }" class="v2-link">{{ $t('system_page.where.sort_all') }}</router-link>
          </div>
          <div class="sp-sec__foot">
            <button v-if="canEditContracts" type="button" class="v2-link" qid="system-link-contract" @click="$emit('link-contract')">
              <font-awesome-icon icon="plus"/>{{ $t('system_page.where.link_contract') }}
            </button>
          </div>
        </template>
      </section>

      <!-- Data held -->
      <section v-if="modules.gdpr" class="v2-card sp-sec" qid="system-data-held">
        <div class="sp-sec__head">
          <font-awesome-icon icon="database"/>
          <h3>{{ $t('system_page.data.title') }}</h3>
          <span class="sp-pips"><i :class="{ 'is-no': missing('data_categories') }"></i></span>
          <span class="sp-sec__state">{{ sectionState(['data_categories']) }}</span>
        </div>
        <div class="sp-kv">
          <div class="sp-kv__k">{{ $t('system_page.data.structure') }}</div>
          <div class="sp-kv__v">
            <span v-if="system.is_structured" class="sp-chip">{{ $t('systems.table.structured') }}</span>
            <span v-if="system.is_unstructured" class="sp-chip">{{ $t('systems.table.unstructured') }}</span>
            <span v-if="system.is_physical" class="sp-chip">{{ $t('systems.table.physical') }}</span>
            <span v-if="!system.is_structured && !system.is_unstructured && !system.is_physical" class="sp-muted">–</span>
          </div>
        </div>
        <div class="sp-kv" :class="{ 'is-missing': missing('data_categories') }">
          <div class="sp-kv__k">{{ $t('system_page.data.categories') }}<span class="sp-req"></span></div>
          <div class="sp-kv__v">
            <span v-for="name in summary.data_categories.slice(0, 8)" :key="name" class="sp-chip" :class="{ 'is-sens': summary.sensitive_data_categories.includes(name) }">{{ name }}</span>
            <span v-if="summary.data_categories.length > 8" class="sp-muted">+{{ summary.data_categories.length - 8 }}</span>
            <span v-if="!summary.data_categories.length" class="sp-muted">{{ $t('system_page.data.none') }}</span>
            <button type="button" class="v2-link sp-sub" @click="$emit('open-tab', 'data')">{{ $t('system_page.data.manage') }}</button>
          </div>
        </div>
      </section>

      <!-- Security and continuity -->
      <section class="v2-card sp-sec" qid="system-security">
        <div class="sp-sec__head">
          <font-awesome-icon icon="shield-alt"/>
          <h3>{{ $t('system_page.security.title') }}</h3>
        </div>
        <div class="sp-kv">
          <div class="sp-kv__k">{{ $t('system_page.security.criticality') }}</div>
          <div class="sp-kv__v sp-cia">
            <span v-for="key in ['confidentiality', 'integrity', 'availability']" :key="key">
              <b :class="ciaClass(summary.criticality[key])" :title="summary.criticality[key] ? $t('impact.' + impactKey(summary.criticality[key])) : ''">{{ impactLevel(summary.criticality[key]) || '–' }}</b>{{ $t('systems_overview.criticality.' + key) }}
            </span>
          </div>
        </div>
        <div class="sp-kv">
          <div class="sp-kv__k">{{ $t('systems_overview.columns.recovery') }}</div>
          <div class="sp-kv__v">
            <b v-if="summary.rto">RTO {{ summary.rto.number }} {{ summary.rto.type }}</b>
            <b v-if="summary.rpo">RPO {{ summary.rpo.number }} {{ summary.rpo.type }}</b>
            <span v-if="!summary.rto && !summary.rpo" class="sp-muted">{{ $t('systems_overview.recovery.none') }}</span>
          </div>
        </div>
        <div class="sp-kv">
          <div class="sp-kv__k">{{ $t('system_page.security.measures') }}</div>
          <div class="sp-kv__v">
            <span class="sp-chip">{{ $tc('system_page.security.measures_count', summary.counts.measures, { count: summary.counts.measures }) }}</span>
            <button type="button" class="v2-link sp-sub" @click="$emit('open-tab', 'controls')">{{ $t('system_page.security.open') }}</button>
          </div>
        </div>
      </section>
    </div>

    <div class="sp-side">
      <!-- Where it's used -->
      <section class="v2-card sp-side__card" qid="system-where-used">
        <h4><font-awesome-icon icon="link"/>{{ $t('system_page.used.title') }}</h4>
        <ul class="sp-used">
          <li v-for="item in usedItems" :key="item.key" :class="'is-' + item.module">
            <b>{{ item.count }}</b>{{ $tc('system_page.used.' + item.key, item.count) }}
          </li>
        </ul>
        <div class="sp-legend">
          <span v-if="modules.gdpr || modules.contracts"><i class="is-gdpr"></i>{{ $t('system_page.used.privacy') }}</span>
          <span v-if="modules.risk"><i class="is-grc"></i>{{ $t('system_page.used.risk') }}</span>
        </div>
        <button type="button" class="v2-link sp-open" qid="system-open-where-used" @click="$emit('open-tab', 'where-used')">
          {{ $t('system_where_used.open') }} <font-awesome-icon icon="arrow-right"/>
        </button>
      </section>

      <!-- Keeping it current -->
      <section v-if="modules.projects" class="v2-card sp-side__card" qid="system-keeping-current">
        <h4><font-awesome-icon icon="sync-alt"/>{{ $t('system_page.current.title') }}</h4>
        <div v-if="summary.open_task" class="sp-alert" :class="summary.open_task.overdue ? 'is-late' : 'is-open'">
          <font-awesome-icon :icon="summary.open_task.overdue ? 'exclamation-triangle' : 'clock'"/>
          <span>{{ summary.open_task.overdue ? $t('system_page.current.overdue') : $t('system_page.current.open') }}</span>
        </div>
        <div class="sp-row">
          <span>{{ $t('system_page.current.covered_by') }}</span>
          <router-link v-if="coveringProject" :to="{ name: 'projects.view', params: { slug: coveringProject.slug } }">{{ coveringProject.title }}</router-link>
          <b v-else>{{ $t('system_page.current.no_project') }}</b>
        </div>
        <div class="sp-row">
          <span>{{ $t('systems_overview.columns.confirmed') }}</span>
          <b>{{ summary.last_confirmed ? formatDate(summary.last_confirmed) : $t('systems_overview.confirmed.never') }}</b>
        </div>
        <div class="sp-row">
          <span>{{ $t('system_page.current.task_goes_to') }}</span>
          <b>{{ taskGoesTo }}</b>
        </div>
        <button v-if="!summary.open_task" type="button" class="btn btn-success btn-sm mt-2" qid="system-get-updated" @click="$emit('get-updated')">
          <font-awesome-icon icon="sync-alt"/> {{ $t('system_page.current.get_updated') }}
        </button>
      </section>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {impactClass, impactLevel, impactKey} from '@/util/impactLevels.js';
  import moment from 'moment';

  const RUNS_ON = ['software_hosting', 'hosting', 'licence'];
  const ACCESS = ['support', 'works_in'];

  export default {
    name: 'SystemOverviewTab',
    props: {
      system: { type: Object, required: true }
    },
    data: function() {
      return {
        modules: {},
        summary: null,
        location: '',
        runsAsValues: ['premises', 'hosted', 'saas', 'physical'],
        coversRunsOn: RUNS_ON,
        coversAccess: ACCESS
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      canEdit() {
        return this.isGranted([this.permissions.SYSTEM_EDIT, this.permissions.SYSTEM_EDIT_OWN], this.system) && (!this.getClient.parent || this.system.partition);
      },
      canEditContracts() {
        return this.isGranted([this.permissions.SYSTEM_THIRD_PARTY_EDIT, this.permissions.SYSTEM_THIRD_PARTY_EDIT_OWN], this.system) && (!this.getClient.parent || this.system.partition);
      },
      needsLocation() {
        return ['premises', 'physical'].includes(this.summary.runs_as);
      },
      needsContract() {
        return ['saas', 'hosted'].includes(this.summary.runs_as);
      },
      runsAsHelp() {
        if (!this.modules.contracts) {
          return this.$t('system_page.where.help_basic');
        }
        return this.needsLocation ? this.$t('system_page.where.help_premises') : this.$t('system_page.where.help');
      },
      contractGroups() {
        let links = this.summary.contracts || [];
        return [
          { key: 'runs_on', links: links.filter(link => RUNS_ON.includes(link.covers)) },
          { key: 'access', links: links.filter(link => ACCESS.includes(link.covers)) },
          { key: 'unsorted', links: links.filter(link => !link.covers) }
        ];
      },
      unsortedCount() {
        return this.contractGroups[2].links.length;
      },
      usedItems() {
        let counts = this.summary.counts;
        let items = [];
        if (this.modules.gdpr) {
          items.push({ key: 'activities', count: counts.activities, module: 'gdpr' });
          items.push({ key: 'processes', count: counts.processes, module: 'gdpr' });
          items.push({ key: 'data_categories', count: counts.data_categories, module: 'gdpr' });
        }
        if (this.modules.contracts) {
          items.push({ key: 'contracts', count: counts.contracts, module: 'gdpr' });
        }
        if (this.modules.risk) {
          items.push({ key: 'risks', count: counts.risks, module: 'grc' });
        }
        items.push({ key: 'measures', count: counts.measures, module: 'grc' });
        return items;
      },
      coveringProject() {
        if (this.summary.open_task) {
          return { title: this.summary.open_task.project, slug: this.summary.open_task.project_slug };
        }
        return this.summary.last_project;
      },
      taskGoesTo() {
        if (!this.summary.owner && !this.summary.members.length) {
          return this.$t('system_page.current.nobody');
        }
        return this.summary.members.length ? this.$t('system_page.current.owner_and_contributors') : this.$t('system_page.current.owner');
      }
    },
    watch: {
      system: function(value, old) {
        if (!old || value.updated_at !== old.updated_at) {
          this.loadSummary();
        }
      }
    },
    methods: {
      loadSummary() {
        let self = this;
        if (!this.system.slug || !this.getClient.slug) {
          return;
        }
        piincHttp.get('systems/' + this.system.slug + '/summary', { params: { client: this.getClient.slug } }).then(function(response) {
          self.modules = response.data.modules;
          self.summary = response.data.system;
          self.location = self.summary.location || '';
          self.$emit('summary', response.data.system);
        }, function() {});
      },
      missing(key) {
        return this.summary.missing.includes(key);
      },
      // One pip per field that applies here: done or still missing
      pips(keys) {
        let applies = {
          runs_as: true,
          location: this.needsLocation,
          contract: this.modules.contracts && this.needsContract
        };
        return keys.filter(key => applies[key]).map(key => !this.missing(key));
      },
      sectionState(keys) {
        let missing = keys.filter(key => this.missing(key)).length;
        return missing ? this.$tc('systems_overview.status.missing', missing, { count: missing }) : this.$t('systems_overview.status.complete');
      },
      // Stored as text levels ("Very low"…"Very high"), shown as 1–4
      ciaClass: impactClass,
      impactLevel: impactLevel,
      impactKey: impactKey,
      formatDate(value) {
        return moment(value).format('D MMM YYYY');
      },
      save(data) {
        let self = this;
        piincHttp.put('systems/' + this.system.slug, data).then(function(response) {
          self.$toastr('success', self.$t('systems_overview.saved'));
          self.afterSave(response.data.slug);
        }, function() {});
      },
      setRunsAs(value) {
        if (value !== this.summary.runs_as) {
          this.save({ runs_as: value });
        }
      },
      saveLocation() {
        if ((this.summary.location || '') !== this.location.trim()) {
          this.save({ location: this.location.trim() || null });
        }
      },
      setCovers(link, value) {
        let self = this;
        piincHttp.put('systems/' + this.system.slug + '/third-parties/' + link.link_id + '/covers', { covers: value }).then(function(response) {
          self.$toastr('success', self.$t('systems_overview.saved'));
          self.afterSave(response.data.slug);
        }, function() {});
      },
      unlink(link) {
        let self = this;
        this.$bvModal.msgBoxConfirm(this.$t('system_link.unlink_text', { contract: link.name, system: this.system.name }), {
          title: this.$t('system_link.unlink_title'),
          okTitle: this.$t('system_link.unlink'),
          okVariant: 'danger',
          cancelTitle: this.$t('systems.create.cancel'),
          centered: true
        }).then(function(ok) {
          if (!ok) {
            return;
          }
          piincHttp.delete('systems/' + self.system.slug + '/third-parties/' + link.link_id).then(function() {
            self.$toastr('success', self.$t('system_link.unlinked'));
            self.afterSave();
          }, function() {});
        });
      },
      // Saving can give the system a new slug (it follows the name); then the page moves to the new address
      afterSave(slug) {
        if (slug && slug !== this.system.slug) {
          this.$router.replace({ name: 'systems.view', params: { slug: slug } });
          return;
        }
        this.$emit('updated');
        this.loadSummary();
      }
    },
    created() {
      this.loadSummary();
    }
  }
</script>
